// DamageMultiplierEvent.tsx

import React, { useContext } from 'react';
import { BaseRendererProps } from './eventRenderers';
import './DamageMultiplierEvent.css';
import GameAssetsContext from '../../../../../../context/GameAssetContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTurnUp } from '@fortawesome/free-solid-svg-icons';
import { getUniversalSourceIcon } from './StatChangeEvent';


//Todo, change this so that they are rendered conditionally under each spell that uses them, as they have selective mutliplication and not universal one
//but not entirely sure on this one

const DamageMultiplierEvent: React.FC<BaseRendererProps> = ({ log }) => {
  const { gameWideAssets, championsAssets } = useContext(GameAssetsContext);

  const details = log.details;
  const damageIncreasePercent = details.damageIncreasePercent || 0;  // e.g. 10 => +10%
  const extraDamage = details.totalExtraDamage || details.extraDamage || 0; // Use aggregated total or fallback to single
  const multiplierName = details.multiplierName;                     // e.g. "Coup de Grace"
  const source = details.source;                                     // e.g. "Coup de Grace"
  const sourceType = details.sourceType || 'Rune';                   // default is Rune
  const affectedDamageTypes = details.affectedDamageTypes || [];     // damage types affected
  const instanceCount = details.instanceCount || 1;                  // number of instances aggregated
  const instances = details.instances || [];                         // individual instance data

  // Use the universal image fetching function
  const sourceIcon = getUniversalSourceIcon(
    {
      sourceType: sourceType,
      imageState: log.imageState,
      sourceName: source,
      championName: details.championName,
    },
    gameWideAssets,
    championsAssets
  );

  // Translate damage type enum values to human-readable names
  const translateDamageType = (damageType: string | number): string => {
    // Handle both string and numeric enum values
    const typeStr = damageType.toString();
    switch (typeStr) {
      case 'Physical':
      case '0':
        return 'Physical Damage';
      case 'Magic':
      case '1':
        return 'Magic Damage';
      case 'True':
      case '2':
        return 'True Damage';
      case 'AdaptiveDamage':
      case '3':
        return 'Adaptive Damage';
      default:
        return typeStr; // fallback to original value
    }
  };

  // Create tooltip text with translated damage types and instance breakdown
  const translatedDamageTypes = affectedDamageTypes.map(translateDamageType);
  
    let tooltipText = `${multiplierName}: +${damageIncreasePercent.toFixed(1)}% damage multiplier`;
  
  if (instanceCount > 1) {
    tooltipText += ` (${instanceCount} instances)`;
    tooltipText += `\nTotal: +${Math.floor(extraDamage)} raw damage`;
    tooltipText += `\nBreakdown:`;
    instances.forEach((instance: any, index: number) => {
      const instanceName = instance.damageInstanceName || `Instance ${index + 1}`;
      tooltipText += `\n  ${instanceName}: +${Math.floor(instance.extraDamage)}`;
    });
  } else {
    // For single instances, show the damage and source name
    const singleInstanceName = instances.length > 0 ? instances[0].damageInstanceName : null;
    tooltipText += ` (+${Math.floor(extraDamage)} raw damage)`;
    if (singleInstanceName) {
      tooltipText += `\nSource: ${singleInstanceName}`;
    }
  }
  
  if (translatedDamageTypes.length > 0) {
    tooltipText += `\nApplies to: ${translatedDamageTypes.join(', ')}`;
  }

  // Format percentage to remove .0 but keep other decimals
  const formatPercentage = (value: number): string => {
    const rounded = Math.round(value * 10) / 10; // Round to 1 decimal place
    return rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(1);
  };

  return (
    <div className="damage-multiplier-event" title={tooltipText}>
      {/* First row: The damage icon + percentage amount */}
      <div className="damage-multiplier-amount-wrapper">
        <div className="damage-icon"></div>
        <span className="damage-multiplier-amount">
          +{formatPercentage(damageIncreasePercent)}%
        </span>
      </div>

      {/* Second row: Raw damage amount */}
      {extraDamage > 0 && (
        <div className="damage-multiplier-raw-wrapper">
          <span className="damage-multiplier-raw">
            (+{Math.floor(extraDamage)})
          </span>
        </div>
      )}

      {/* Second row: The arrow + source icon */}
      {sourceIcon && (
        <div className="damage-multiplier-source-wrapper">
          <FontAwesomeIcon icon={faTurnUp} className="fa-turn-up" />
          {sourceIcon}
        </div>
      )}
    </div>
  );
};

export default DamageMultiplierEvent;
